import { Routes, Route, Navigate } from "react-router-dom";
import { useContext } from "react";
import Layout from "../Component/Layout/Layout";
import Login from "../Component/Login/Login";
import Dashboard from "../Component/Dashboard/Dashboard";
import BeneficiaryManagement from "../Component/BeneficiaryManagement/BeneficiaryManagement";
import TreatmentModule from "../Component/TreatmentModule/TreatmentModule";
import AppointmentManagement from "../Component/MoComponents/AppointmentManagement/AppointmentManagement";
import Dashboradmo from "../Component/MoComponents/DashboardMo/DashboardMo";
import { paths } from "../utils/path";
import { AuthContext } from "../context/AuthContext";
import PatientRegistration from "../Component/PatientRegistration/PatientRegistration";
import { canAccessPage, getDashboard, getDashboardPath } from "../config/rolesConfig";
import TreatmentProcedure from "../Component/MoComponents/TreatmentProcedure/TreatmentProcedure";
import TreatmentProcedureStep from "../Component/MoComponents/TreatmentProcedureStep/TreatmentProcedureStep";
import AdminDashboard from "../Component/Dashboard/AdminDashboard/AdminDashboard";
import StatewiseStatsTable from "../Component/Dashboard/AdminDashboard/StatewiseStatsTable/StatewiseStatsTable";
import ProfileView from "../Component/ProfileView/ProfileView";
import HrManagement from "../Component/HrManagement/HrManagement";
import LoginWithoutHomepage from "../Component/Login/LoginWithoutHomepage";
import StateDashboard from "../Component/Dashboard/StateDashboard/StateDashboard";
import DownloadReport from "../Component/DownloadReport/DownloadReport";
import AdminReportsDownload from "../Component/DownloadReport/AdminReportsDownload/AdminReportsDownload";
import StateCredDownload from "../Component/DownloadReport/StateCredDownload/StateCredDownload";
import MedicineList from "../Component/Dashboard/AdminDashboard/MedicineList/MedicineList";

// Component to handle protected routes
const ProtectedLayout = () => {
    const { isAuthenticated } = useContext(AuthContext);

    if (!isAuthenticated) return <Navigate to={paths?.login} replace />;
    return <Layout />;
};

// Protected Route - redirects if no permission
const ProtectedRoute = ({ children, page }) => {
    const { isAuthenticated, userRole } = useContext(AuthContext);

    if (!isAuthenticated) {
        return <Navigate to={paths?.login} replace />;
    }

    // If user can't access this page, redirect to their dashboard
    if (!canAccessPage(userRole, page)) {
        const userDashboardPath = getDashboardPath(userRole);
        return <Navigate to={`${paths?.user?.commonPoint}${paths?.user?.[userDashboardPath]}`} replace />;
    }

    return children;
};

// Dashboard Router - shows appropriate dashboard based on role
const DashboardRouter = () => {
    const { userRole } = useContext(AuthContext);
    const dashboardType = getDashboard(userRole);

    // Check which dashboard user should see
    if (canAccessPage(userRole, "dashboard")) {
        // Mentoring institute sees Dashboard component
        return <Dashboard />;
    } else if (canAccessPage(userRole, "genericDashboard")) {
        // Admin, State, National see generic welcome
        return (
            <div className="ms-5 mt-5 ps-5">
                <h3>Welcome to the Dashboard</h3>
                <p>Role: <strong>{userRole}</strong> User</p>
            </div>
        );
    } else {
        // DCCC or others - redirect to their dashboard
        const userDashboardPath = getDashboardPath(userRole);
        return <Navigate to={`${paths?.user?.commonPoint}${paths?.user?.[userDashboardPath]}`} replace />;
    }
};

// Main Routes Component
const AppRoutes = () => {
    const { isAuthenticated } = useContext(AuthContext);
    return (
        <Routes>
            {/* Login page */}
            <Route
                path={paths?.login}
                element={
                    isAuthenticated ?
                        <Navigate to={`${paths?.user?.commonPoint}${paths?.user?.dashboard}`} replace /> :
                        // <Login />
                        <LoginWithoutHomepage />
                }
            />

            {/* Protected routes */}
            <Route element={<ProtectedLayout />}>
                {/* Dashboard Route - handles Mentoring institute and Admin/State/National */}
                <Route
                    path={`${paths?.user?.commonPoint}${paths?.user?.dashboard}`}
                    element={<DashboardRouter />}
                />

                {/* MO Dashboard for DCCC */}
                <Route
                    path={`${paths?.user?.commonPoint}${paths?.user?.moDashboard}`}
                    element={
                        <ProtectedRoute page="moDashboard">
                            <Dashboradmo />
                        </ProtectedRoute>
                    }
                />

                {/* Admin Dashboard for DCCC */}
                <Route
                    path={`${paths?.user?.commonPoint}${paths?.user?.adminDashboard}`}
                    element={
                        <ProtectedRoute page="adminDashboard">
                            <AdminDashboard />
                        </ProtectedRoute>
                    }
                />
                {/* State Dashboard for DCCC */}
                <Route
                    path={`${paths?.user?.commonPoint}${paths?.user?.stateDashboard}`}
                    element={
                        <ProtectedRoute page="stateDashboard">
                            <StateDashboard />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path={`${paths?.user?.commonPoint}${paths?.user?.adminStatewiseStats}`}
                    element={
                        <ProtectedRoute page="adminStatewiseStats">
                            <StatewiseStatsTable />
                        </ProtectedRoute>
                    }
                />

                {/* <Route
                    path={`${paths?.user?.commonPoint}${paths?.user?.downloadData}`}
                    element={
                        <ProtectedRoute page="downloadData">
                            <DownloadReport />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path={`${paths?.user?.commonPoint}${paths?.user?.moDownloadData}`}
                    element={
                        <ProtectedRoute page="moDownloadData">
                            <DownloadReport />
                        </ProtectedRoute>
                    }
                /> */}
                <Route
                    path={`${paths?.user?.commonPoint}${paths?.user?.adminDownloadData}`}
                    element={
                        <ProtectedRoute page="adminDownloadData">
                            <AdminReportsDownload />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path={`${paths?.user?.commonPoint}${paths?.user?.adminMedicineList}`}
                    element={
                        <ProtectedRoute page="adminMedicineList">
                            <MedicineList />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path={`${paths?.user?.commonPoint}${paths?.user?.stateCredDownload}`}
                    element={
                        <ProtectedRoute page="stateCredDownload">
                            <StateCredDownload />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path={`${paths?.user?.commonPoint}${paths?.user?.beneficiaryManagement}`}
                    element={
                        <ProtectedRoute page="beneficiaryManagement">
                            <BeneficiaryManagement />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path={`${paths?.user?.commonPoint}${paths?.user?.moBeneficiaryManagement}`}
                    element={
                        <ProtectedRoute page="moBeneficiaryManagement">
                            <BeneficiaryManagement />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path={`${paths?.user?.commonPoint}${paths?.user?.treatmentModule}`}
                    element={
                        <ProtectedRoute page="treatmentModule">
                            <TreatmentModule />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path={`${paths?.user?.commonPoint}${paths?.user?.treatmentProcedure}`}
                    element={
                        <ProtectedRoute page="treatmentProcedure">
                            <TreatmentProcedure />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path={`${paths?.user?.commonPoint}${paths?.user?.miTreatmentProcedure}`}
                    element={
                        <ProtectedRoute page="miTreatmentProcedure">
                            <TreatmentProcedure />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path={`${paths?.user?.commonPoint}${paths?.user?.treatmentProcedureStep}`}
                    element={
                        <ProtectedRoute page="treatmentProcedureStep">
                            <TreatmentProcedureStep />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path={`${paths?.user?.commonPoint}${paths?.user?.appointmentManagement}`}
                    element={
                        <ProtectedRoute page="appointmentManagement">
                            <AppointmentManagement />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path={`${paths?.user?.commonPoint}${paths?.user?.patientRegistration}`}
                    element={
                        <ProtectedRoute page="patientRegistration">
                            <PatientRegistration />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path={`${paths?.user?.commonPoint}${paths?.user?.profileView}`}
                    element={
                        <ProtectedRoute page="profileView">
                            <ProfileView />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path={`${paths?.user?.commonPoint}${paths?.user?.hrManagement}`}
                    element={
                        <ProtectedRoute page="hrManagement">
                            <HrManagement />
                        </ProtectedRoute>
                    }
                />
            </Route>

            <Route
                path="*"
                element={
                    isAuthenticated ?
                        <Navigate to={`${paths?.user?.commonPoint}${paths?.user?.dashboard}`} replace /> :
                        <Navigate to={paths?.login} replace />
                }
            />
        </Routes>
    );
};

export default AppRoutes;